import { CircleX, Clock, ShieldAlert, TriangleAlert } from 'lucide-react';
import { Link } from 'react-router';
import type { Deployment, Run } from '../api/types';
import { DiagnosticList } from '../components/ui';
import { findPolicyDenial } from '../lib/policyDenial';
import { TRIGGER_WAITING_FOR_CODE } from '../lib/triggers';
import { NetworkErrorAction } from '../networks/deployment/NetworkProblem';
import { isGrantPending, networkErrorInfo } from '../networks/shared';
import { strings } from '../strings';

const r = strings.runPage;

/**
 * A run's failure and warnings with their resolving actions (spec 6.5): `SECRET_VALUE_MISSING` → the secrets view,
 * `PLAN_STALE` → Plan again, waiting triggers → Show on canvas, organization policy denials named as such.
 */
export function RunIssues({
  run,
  deployment,
  onPlanAgain,
  logErrors = [],
  network,
}: {
  run: Run;
  deployment: Deployment | undefined;
  onPlanAgain: () => void;
  /** Error log lines, searched for policy denials. */
  logErrors?: readonly string[];
  /** Feature spec 17: the run's network (else the deployment's), for the networking errors' sentences. */
  network?: { id: string; name: string } | null;
}) {
  const diagnostics = run.result?.diagnostics ?? [];
  const errors = diagnostics.filter((d) => d.severity === 'error');
  // Feature spec 17, phase 2: pending network requests have their own callouts in the plan review (6.15.23).
  const warnings = diagnostics.filter((d) => d.severity !== 'error' && d.code !== 'NETWORK_REQUEST_PENDING');
  const failed = run.status === 'failed' || !!run.error;
  const codes = new Set([run.error?.code, ...errors.map((d) => d.code)]);
  const denial = failed
    ? findPolicyDenial([run.error?.message, ...errors.map((d) => d.message), ...logErrors])
    : null;
  const waiting = warnings.filter((d) => d.code === TRIGGER_WAITING_FOR_CODE);
  // Feature spec 17 (6.15.17, 6.15.18): a connect run that waits for permissions isn't a red failure; networking codes
  // get their sentence and role-aware action.
  const net = network ?? deployment?.network ?? null;
  const subject = deployment?.name ?? null;
  const subjectId = deployment?.id ?? run.attachmentDeploymentId ?? run.deploymentId ?? '';
  const info = run.error
    ? networkErrorInfo(run.error.code, { network: net?.name, deployment: subject })
    : null;
  if (isGrantPending(run))
    return (
      <p role="status" className="callout callout-info" data-testid="run-grant-pending">
        <Clock aria-hidden className="size-4 shrink-0" />
        <span>
          {strings.networks.waiting.grantPendingCallout(subject ?? strings.networks.runLabels.aDeployment)}
        </span>
      </p>
    );
  return (
    <>
      {failed && (
        <div role="alert" className="callout callout-danger" data-testid="run-failure">
          <CircleX aria-hidden className="size-4 shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="font-semibold">{r.failed(strings.status.runKind[run.kind])}</p>
            {run.error && (
              <p>
                <code className="font-mono font-semibold">{run.error.code}</code>: {run.error.message}
              </p>
            )}
            {run.error && info && (
              <p className="mt-1 flex flex-wrap items-center gap-2" data-testid="run-network-error">
                <span>{info.sentence}</span>
                <NetworkErrorAction
                  code={run.error.code}
                  networkId={net?.id}
                  network={net?.name}
                  deploymentId={subjectId}
                  deployment={subject ?? strings.networks.runLabels.aDeployment}
                />
              </p>
            )}
            {errors.length > 0 && (
              <div className="mt-2">
                <DiagnosticList diagnostics={errors} />
              </div>
            )}
            {codes.has('SECRET_VALUE_MISSING') && (
              <p className="mt-2">
                {strings.runs.setMissingValues}{' '}
                <Link className="link underline" to={`/deployments/${run.deploymentId}?tab=secrets`}>
                  {strings.runs.openSecrets}
                </Link>
              </p>
            )}
            {codes.has('PLAN_STALE') && (
              <button type="button" className="btn btn-sm mt-2" onClick={onPlanAgain}>
                {r.planAgain}
              </button>
            )}
            {denial && (
              <p className="callout callout-warning mt-2" data-testid="policy-denial">
                <ShieldAlert aria-hidden className="size-4 shrink-0" />
                <span>
                  <span className="font-semibold">{r.policyDenial(denial.cloud, denial.policy)}</span>{' '}
                  {r.policyDenialText}
                </span>
              </p>
            )}
          </div>
        </div>
      )}
      {warnings.length > 0 && (
        <div className="callout callout-warning" data-testid="run-warnings">
          <TriangleAlert aria-hidden className="size-4 shrink-0" />
          <div className="min-w-0 flex-1">
            <DiagnosticList diagnostics={warnings} />
            {waiting.length > 0 && (
              <p className="mt-2 flex flex-wrap items-center gap-2">
                {strings.designer.waitingForCodeHelp}
                {deployment && (
                  <Link
                    className="btn btn-sm"
                    to={`/blueprints/${deployment.blueprintId}?deployment=${deployment.id}${waiting[0].resourceId ? `&select=${waiting[0].resourceId}` : ''}`}
                  >
                    {strings.planReview.showOnCanvas}
                  </Link>
                )}
              </p>
            )}
          </div>
        </div>
      )}
    </>
  );
}
